import { useState, useEffect } from 'react';

// Product interface matching architecture specification
interface PaymentTerms {
  pricePerUnitUsd: number;
  totalUnits: number;
  totalVolumeMt: number;
  totalDueUsd: number;
}

interface Product {
  id: string;
  name: string;
  paymentTerms: PaymentTerms;
}

interface BuyFlowStep2Props {
  productId: string;
  onStateChange?: (data: { termsReviewed: boolean }) => void;
}

// Mock product data - matches ProductDetailPage pattern
const mockProducts: Product[] = [
  {
    id: '1',
    name: 'Brent Crude FOB',
    paymentTerms: {
      pricePerUnitUsd: 85.50,
      totalUnits: 100,
      totalVolumeMt: 100,
      totalDueUsd: 8550.00,
    },
  },
  {
    id: '2',
    name: 'Diesel Grade A',
    paymentTerms: {
      pricePerUnitUsd: 92.00,
      totalUnits: 50,
      totalVolumeMt: 50,
      totalDueUsd: 4600.00,
    },
  },
  {
    id: '3',
    name: 'WTI Crude',
    paymentTerms: {
      pricePerUnitUsd: 84.00,
      totalUnits: 200,
      totalVolumeMt: 200,
      totalDueUsd: 16800.00,
    },
  },
];

/**
 * Buy Flow Step 2: Review Terms
 * 
 * Displays transaction summary (volume, price per unit, total cost, product name)
 * before proceeding to 2FA verification.
 * 
 * Features:
 * - Transaction summary with large, readable numbers
 * - Auto-calculated total cost (volume × pricePerUnit)
 * - Currency formatting using Intl.NumberFormat
 * - Terms match product detail page information
 * - Responsive layout (centered card, max-width 600px)
 * - Loading and error states
 * - Accessibility features (ARIA labels, keyboard navigation, screen reader support)
 * 
 * Integration:
 * - Works within WizardLayout wrapper (from Story 5.1)
 * - Receives productId from BuyWizard
 * - Reports terms reviewed state via onStateChange callback
 * - Navigation handled by WizardLayout (Back, Continue, Cancel buttons)
 */
export const BuyFlowStep2 = ({ productId, onStateChange }: BuyFlowStep2Props) => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch product data (mock implementation)
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);

      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 500));

      if (!productId) {
        setError('Product ID is required');
        setLoading(false);
        return;
      }

      const foundProduct = mockProducts.find(p => p.id === productId);
      
      if (!foundProduct) {
        setError('Product not found');
        setLoading(false);
        return;
      }

      setProduct(foundProduct);
      setLoading(false);
    };

    fetchProduct();
  }, [productId]);

  // Report terms reviewed state to parent
  useEffect(() => {
    if (product && onStateChange) {
      // Terms are considered reviewed when product data is loaded
      onStateChange({ termsReviewed: true });
    }
  }, [product, onStateChange]);

  // Format currency using Intl.NumberFormat (matches ProductDetailPage pattern)
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  // Format volume with unit
  const formatVolume = (volume: number): string => {
    return `${volume} MT`;
  };

  // Calculate total cost (volume × pricePerUnit)
  // Note: This should match product.paymentTerms.totalDueUsd for verification
  const calculateTotalCost = (): number => {
    if (!product) return 0;
    const calculated = product.paymentTerms.totalVolumeMt * product.paymentTerms.pricePerUnitUsd;
    // Verify calculation matches product detail page (for development/debugging)
    if (Math.abs(calculated - product.paymentTerms.totalDueUsd) > 0.01) {
      console.warn('Calculated total cost does not match product totalDueUsd', {
        calculated,
        expected: product.paymentTerms.totalDueUsd,
      });
    }
    return calculated;
  };

  // Loading state
  if (loading) {
    return (
      <div className="w-full flex items-center justify-center py-32">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p className="text-base text-text-secondary">Loading transaction terms...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !product) {
    return (
      <div className="w-full flex items-center justify-center py-32">
        <div className="text-center">
          <p className="text-h2 font-bold text-text-primary mb-2">Unable to Load Terms</p>
          <p className="text-base text-text-secondary mb-6">{error || 'Product information could not be loaded.'}</p>
        </div>
      </div>
    );
  }

  const totalCost = calculateTotalCost();

  return (
    <div className="w-full">
      {/* Screen reader announcements region */}
      <div
        id="screen-reader-announcements-step2"
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />

      {/* Title */}
      <h1 className="text-3xl font-bold text-text-primary mb-2">Review Terms</h1>

      {/* Instruction */}
      <p className="text-base text-text-secondary mb-8">
        Please review the transaction terms before proceeding
      </p>

      {/* Transaction Summary Card - Centered, max-width 600px */}
      <div className="max-w-[600px] mx-auto">
        <section
          className="bg-background border border-border rounded-lg shadow-md p-6 lg:p-8"
          role="region"
          aria-labelledby="transaction-summary-heading"
          aria-label="Transaction summary"
        >
          <h2 id="transaction-summary-heading" className="sr-only">
            Transaction Summary
          </h2>

          {/* Product Name */}
          <div className="mb-6">
            <label 
              htmlFor="product-name-display"
              className="text-sm font-medium text-text-secondary block mb-2"
            >
              Product
            </label>
            <p 
              id="product-name-display"
              className="text-xl font-semibold text-text-primary"
              aria-label={`Product name: ${product.name}`}
            >
              {product.name}
            </p>
          </div>

          <hr className="border-border mb-6" aria-hidden="true" />

          {/* Transaction Summary Fields */}
          <dl className="space-y-6" role="list" aria-label="Transaction terms">
            {/* Volume */}
            <div role="listitem">
              <dt className="text-sm font-medium text-text-secondary mb-2">
                Volume
              </dt>
              <dd 
                className="text-2xl font-semibold text-text-primary"
                aria-label={`Volume: ${formatVolume(product.paymentTerms.totalVolumeMt)}`}
              >
                {formatVolume(product.paymentTerms.totalVolumeMt)}
              </dd>
            </div>

            {/* Price per unit */}
            <div role="listitem">
              <dt className="text-sm font-medium text-text-secondary mb-2">
                Price per unit
              </dt>
              <dd 
                className="text-2xl font-semibold text-text-primary"
                aria-label={`Price per unit: ${formatCurrency(product.paymentTerms.pricePerUnitUsd)}`}
              >
                {formatCurrency(product.paymentTerms.pricePerUnitUsd)}
              </dd>
            </div>

            {/* Total cost - Highlighted prominently */}
            <div role="listitem" className="pt-4 border-t border-border">
              <dt className="text-sm font-medium text-text-secondary mb-2">
                Total cost
              </dt>
              <dd 
                className="text-4xl font-bold text-primary"
                aria-label={`Total cost: ${formatCurrency(totalCost)}`}
              >
                {formatCurrency(totalCost)}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </div>
  );
};

